import { stageMenuStore } from './store.js';
import { assignRecipeStore } from './store.js';
import { assignRecipes } from './ajax.js';
import { triggerStageMenuReload } from './render.js';
import {
	resetAndLoad,
	syncCheckboxState,
	recipeListContainer,
	searchInput,
	setSearchQuery,
	loadRecipes,
	isLoading,
	hasMore,
} from './form.js';

// ─── Stage menu click delegation ──────────────────────────────────────────────

/**
 * Handle click events via event delegation
 * Routes to delete or add handlers
 * @param {Event} e - Click event
 */
export function handleClick(e) {
	const deleteBtn = e.target.closest('.delete-item');
	if (deleteBtn) {
		handleDeleteClick(deleteBtn);
		return;
	}

	const addBtn = e.target.closest('.add-recipe-btn');
	if (addBtn) {
		handleAddClick(addBtn);
		return;
	}
}

/**
 * Handle delete button click - removes recipe from field
 * @param {HTMLElement} deleteBtn
 */
function handleDeleteClick(deleteBtn) {
	const recipeId = deleteBtn.getAttribute('data-recipe-id');
	if (!recipeId) return;

	const categorySection = deleteBtn.closest('.category-section');
	if (!categorySection) return;

	const fieldName = categorySection.getAttribute('data-field-name');
	if (!fieldName) return;

	const dayMatch = fieldName.match(/^day_(\d+)_/);
	const day = dayMatch ? parseInt(dayMatch[1]) : null;

	removeRecipe(fieldName, recipeId, day);
}

/**
 * Handle add recipe button click - opens popup with current field set
 * @param {HTMLElement} addBtn
 */
function handleAddClick(addBtn) {
	const fieldName = addBtn.getAttribute('data-field-name');
	if (!fieldName) return;

	stageMenuStore.setCurrentFieldName(fieldName);

	const dayMatch = fieldName.match(/^day_(\d+)_/);
	if (dayMatch) {
		stageMenuStore.setCurrentDay(parseInt(dayMatch[1]));
	}

	// eslint-disable-next-line no-undef
	wpcore.showPopup('select-recipes');
}

/**
 * Remove recipe from a field and reload day
 * @param {string}      fieldName
 * @param {string}      recipeId
 * @param {number|null} day
 */
async function removeRecipe(fieldName, recipeId, day) {
	const stageId = stageMenuStore.getStageId();
	const recipeIds = stageMenuStore.getRecipeIdsByField(fieldName);
	const filteredIds = recipeIds.filter((id) => String(id) !== String(recipeId));
	await assignRecipes(stageId, fieldName, filteredIds);
	if (day) {
		triggerStageMenuReload(day);
	}
}

// ─── Popup handlers ───────────────────────────────────────────────────────────

/**
 * Handle popup open - sync selected recipes + reset list
 * @param {CustomEvent} e
 */
export function handlePopupOpen(e) {
	if (e.detail?.target !== 'select-recipes') return;

	const recipeIds = stageMenuStore.getCurrentRecipeIds();
	assignRecipeStore.setSelectedRecipes(recipeIds);

	if (searchInput) searchInput.value = '';
	setSearchQuery('');

	resetAndLoad();
}

/**
 * Infinite scroll handler
 */
export function handleScroll() {
	const container = recipeListContainer;
	if (!container) return;
	const { scrollTop, scrollHeight, clientHeight } = container;
	if (scrollHeight - scrollTop <= clientHeight + 120 && !isLoading && hasMore) {
		loadRecipes();
	}
}

/**
 * Handle checkbox change - add/remove recipe from selection
 * @param {Event} e
 */
export function handleCheckboxChange(e) {
	const checkbox = e.target;
	if (checkbox.type !== 'checkbox') return;

	if (checkbox.checked) {
		assignRecipeStore.addRecipe(checkbox.value);
	} else {
		assignRecipeStore.removeRecipe(checkbox.value);
	}

	syncCheckboxState();
}

/**
 * Handle form submit - assign selected recipes to current field
 * @param {Event} e
 */
export async function handleFormSubmit(e) {
	e.preventDefault();
	const form = e.target;
	const stageId = form.querySelector('input[name="stage-id"]').value;
	const fieldName = stageMenuStore.getCurrentFieldName();
	const day = stageMenuStore.getCurrentDay();
	const recipeIds = assignRecipeStore.getSelectedRecipes();

	const success = await assignRecipes(stageId, fieldName, recipeIds);

	if (success) {
		// eslint-disable-next-line no-undef
		wpcore.closePopup('select-recipes');
		if (day) {
			triggerStageMenuReload(day);
		}
	} else {
		// eslint-disable-next-line no-undef
		alert(
			wpcore.direct_t({
				en: 'Failed to assign recipes',
				fr: "Échec de l'attribution des recettes",
			})
		);
	}
}
